import type { Language } from '@eformulieren/engine'
import type { ReactNode } from 'react'
import { BusyLink } from './BusyButton.js'
import type { Bilingual } from '../language/text.js'

/**
 * De keuze hoe iemand inlogt, als hij een link opent zonder geldige sessie voor deze publicatie
 * (R-154, R-203, R-64c, G017, ontwerp 3.3, opdracht 5c).
 *
 * Een link naar een instantie of naar een uitnodiging is een gewone URL en geen sleutel (R-154):
 * wie hem opent, logt eerst in bij de inlogdienst van dit product. De pod weet niet of het een
 * aanvrager is of een medewerker van NWO, dus hij biedt per ingang een knop, en de inlogdienst
 * stuurt na het inloggen terug naar dezelfde pagina. Is er maar één ingang, dan toch de knop en
 * geen automatische omleiding: de gebruiker ziet waar hij heen gaat.
 *
 * **Het label komt uit de kant en niet uit de naam van de ingang.** Wie op een link klikt weet of
 * hij medewerker van NWO is of aanvrager, en niet hoe de inrichting zijn ingang noemt. Staan er twee
 * ingangen aan dezelfde kant, dan krijgt elk zijn naam erbij, want twee links met dezelfde naam en
 * een ander doel zijn voor een schermlezer niet uit elkaar te houden.
 *
 * **Een link en geen knop met een klikhandler.** Het is een navigatie naar een andere dienst: de
 * statusbalk van de browser toont het adres, en een tweede tabblad openen kan. De stijl is die van
 * een actieknop, zodat het er als de keuze uitziet die het is.
 */

export interface LoginOption {
  /** De naam van de ingang, zoals de pod en de inlogdienst hem kennen. */
  readonly gastheer: string
  readonly kant: 'intern' | 'extern'
  /** `<inlogdienst>/<gastheer>/starten?doel=<het adres van de pagina>`; de pagina rekent het uit. */
  readonly adres: string
}

const T = {
  groep: { nl: 'Kies hoe u inlogt', en: 'Choose how to sign in' },
  intern: { nl: 'Inloggen als medewerker van NWO', en: 'Sign in as an NWO employee' },
  extern: { nl: 'Inloggen als aanvrager', en: 'Sign in as an applicant' },
  /** Op de uitnodigingspagina: wie uitgenodigd is, is geen aanvrager (G-07). */
  uitnodigingExtern: { nl: 'Inloggen', en: 'Sign in' },
  uitnodigingIntern: { nl: 'Ik werk bij NWO', en: 'I work at NWO' },
  titel: { nl: 'Inloggen', en: 'Sign in' },
  uitleg: {
    nl: 'U bent niet ingelogd, of uw sessie voor dit formulier is verlopen. Kies hoe u inlogt; daarna komt u terug op dit formulier.',
    en: 'You are not signed in, or your session for this form has expired. Choose how to sign in; afterwards you return to this form.',
  },
  storing: {
    nl: 'Het lukt nu niet vast te stellen wie u bent. Probeer het over een paar minuten opnieuw.',
    en: 'We cannot establish who you are right now. Please try again in a few minutes.',
  },
  opnieuw: { nl: 'Opnieuw proberen', en: 'Try again' },
} as const satisfies Readonly<Record<string, Bilingual>>

/** Voor wie de knoppen zijn: wie een formulier opent, of wie een uitnodiging kreeg. */
export type LoginFor = 'formulier' | 'uitnodiging'

/** Het label van een knop: de kant, en de naam van de ingang erbij als de kant dubbel voorkomt. */
function labelOf(option: LoginOption, options: readonly LoginOption[], language: Language, voor: LoginFor): string {
  const base =
    voor === 'uitnodiging' ? (option.kant === 'intern' ? T.uitnodigingIntern : T.uitnodigingExtern)[language] : T[option.kant][language]
  const double = options.filter((other) => other.kant === option.kant).length > 1
  return double ? `${base} (${option.gastheer})` : base
}

export interface LoginChoiceProps {
  readonly taal: Language
  readonly opties: readonly LoginOption[]
  /**
   * Op de uitnodigingspagina "Inloggen" en "Ik werk bij NWO" in plaats van "Inloggen als aanvrager":
   * een medeschrijver, meelezer of adviseur is geen aanvrager (G-07, mockup 05 van delen).
   */
  readonly voor?: LoginFor
}

/** De knoppen, één per ingang, in de volgorde van de pod. */
export function LoginChoice({ taal: language, opties: options, voor = 'formulier' }: LoginChoiceProps): ReactNode {
  return (
    <div className="ef-inlogkeuze" role="group" aria-label={T.groep[language]}>
      {options.map((option) => (
        // Eén primaire knop: de ingang van buiten, want daar komen de meeste lezers van een link
        // binnen. Zonder die ingang is de eerste de primaire (audit R-40).
        <BusyLink
          key={option.gastheer}
          className="ef-actieknop ef-inlogkeuze__knop"
          data-soort={option === (options.find((other) => other.kant === 'extern') ?? options[0]) ? 'primair' : 'secundair'}
          href={option.adres}
          busyText={BUSY_LOGIN[language]}
        >
          {labelOf(option, options, language, voor)}
        </BusyLink>
      ))}
    </div>
  )
}

/** Wat het scherm toont: de keuze, of een storing waarbij inloggen niet helpt. */
export type LoginState =
  | { readonly soort: 'inloggen'; readonly opties: readonly LoginOption[] }
  /**
   * De pod kon niet vaststellen of er een sessie is (een 503 van `/whoami`, of geen verbinding),
   * of hij noemde zijn ingangen niet. Opnieuw inloggen helpt dan niet: met een lopende sessie stuurt
   * de inlogdienst meteen terug, en dan staat de gebruiker hier weer.
   */
  | { readonly soort: 'storing' }

export interface LoginScreenProps {
  readonly taal: Language
  readonly stand: LoginState
  readonly onAgain: () => void
}

/** Het scherm voor een directe link naar een formulier zonder sessie. */
export function LoginScreen({ taal: language, stand: state, onAgain }: LoginScreenProps): ReactNode {
  return (
    <section className="ef-inloggen" lang={language} aria-labelledby="ef-inloggen-titel">
      <h1 id="ef-inloggen-titel" className="ef-inloggen__titel">
        {T.titel[language]}
      </h1>
      {state.soort === 'inloggen' ? (
        <>
          <p className="ef-inloggen__tekst">{T.uitleg[language]}</p>
          <LoginChoice taal={language} opties={state.opties} />
        </>
      ) : (
        <Outage taal={language} onAgain={onAgain} />
      )}
    </section>
  )
}

/** De storing, met een knop om het opnieuw te proberen; ook gebruikt door de uitnodiging. */
export function Outage({ taal: language, onAgain }: { readonly taal: Language; readonly onAgain: () => void }): ReactNode {
  return (
    <>
      <p role="alert" className="ef-inloggen__melding">
        {T.storing[language]}
      </p>
      <div className="ef-inloggen__acties">
        <button type="button" className="ef-actieknop" data-soort="secundair" onClick={onAgain}>
          {T.opnieuw[language]}
        </button>
      </div>
    </>
  )
}

/** Wat een inlogknop zegt terwijl de browser naar de inlogdienst gaat (`BusyLink`). */
const BUSY_LOGIN = { nl: 'Bezig met doorsturen…', en: 'Redirecting…' } as const
